import { differenceInMilliseconds } from 'date-fns';

import { Injectable } from '@angular/core';
import { interval, Observable, ReplaySubject, share } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * @ngdoc service
 * @name ApKeepAliveTimerService
 */
@Injectable({
    providedIn: 'root'
})
export class ApKeepAliveTimerService {
    public timePassed$: Observable<number>;
    public heartbeatTimePassed$: Observable<number>;

    private timePassed = 0;
    private heartbeatTimePassed = 0;
    private referenceMoment: Date;
    private heartbeatReferenceMoment: Date;
    private readonly INTERVAL = 1000;

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#start
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Creates the keep alive timer and heartbeat timer as interval observables.
     */
    public start(): void {
        const now: Date = new Date();
        this.referenceMoment = now;
        this.heartbeatReferenceMoment = now;

        this.timePassed$ = interval(this.INTERVAL).pipe(
            map(() => this.increaseTimePassed()),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );

        this.heartbeatTimePassed$ = interval(this.INTERVAL).pipe(
            map(() => this.increaseHeartbeatTimePassed()),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#isRunning
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Check to see if the service has been started yet.
     *
     */
    public isRunning(): boolean {
        return this.timePassed$ !== undefined && this.heartbeatTimePassed$ !== undefined;
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#reset
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Resets the time passed for keep alive to 0.
     *
     */
    public reset(): void {
        this.referenceMoment = new Date();
        this.timePassed = 0;
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#resetHeartbeat
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Resets the time passed for the heartbeat to 0.
     *
     */
    public resetHeartbeat(): void {
        this.heartbeatReferenceMoment = new Date();
        this.heartbeatTimePassed = 0;
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#increaseTimePassed
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Increases the time passed for keep alive with the time interval (1000 ms).
     *
     */
    private increaseTimePassed(): number {
        this.timePassed = differenceInMilliseconds(new Date(), this.referenceMoment);
        return this.timePassed;
    }

    /**
     * @ngdoc method
     * @name ApKeepAliveTimerService#increaseHeartbeatTimePassed
     * @methodOf ApKeepAliveTimerService
     *
     * @description
     * Increases the time passed for the heartbeat with the time interval (1000 ms).
     *
     */
    private increaseHeartbeatTimePassed(): number {
        this.heartbeatTimePassed = differenceInMilliseconds(new Date(), this.heartbeatReferenceMoment);
        return this.heartbeatTimePassed;
    }
}
